import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import stockTransferService from "../services/stockTransferService";
import StorageService from "../services/StorageService";
import Loading from "../components/loading";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import UnAuthorized from "../views/Unathorized";
import ReactPaginate from "react-paginate";
import MessageDialog from "../components/MessageDialog";
import utility from "../utils/utility";
import CompanyService from "../services/companyService";
import ProjectService from "../services/ProjectService";

export default function ReceiveStockTransferMain() {

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [selectedId, setSelectedId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupHeader, setPopupHeader] = useState(null);
  const [popupDetails, setPopupDetails] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");
  const [projectList, setProjectList] = useState([]);
  const selectedTransfer = transfers.find(t => t.id === selectedId);
  const isInTransit = selectedTransfer?.status === "intransit";
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [allTransfers, setAllTransfers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("ALL");
  const itemsPerPage = 10;

  const companyId = StorageService.activeCompany().companyId;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const pRes = await ProjectService.getActive(companyId);
        const pData = pRes?.data || pRes || [];
        setProjectList(Array.isArray(pData) ? pData : []);
      } catch (e) {
        console.error("Failed to load projects:", e);
      }
    };
    if (companyId) {
      fetchProjects();
    }
  }, [companyId]);

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const { permission, can } = usePermission(
    ScreenData.Stock_Transfer,
    showMessage
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return utility.formatDateDDMMMYYYY(dateStr);
  };

  const formatEmployee = (name, code) => {
    if (name && code) return `${name} (${code})`;
    return name || code || "-";
  };

  const formatProject = (name, code, categoryName) => {
    const matchedProject =
      (name && projectList.find((p) => p.projectName === name)) || {};
    const catName = categoryName || matchedProject.projectCategoryName || matchedProject.categoryName || "";
    const projCode = code || matchedProject.projectCode || "";
    const categoryInitial = String(catName || "").trim().charAt(0).toUpperCase();

    if (name) {
      if (categoryInitial) return `${categoryInitial}*${name}`;
      if (projCode) {
        const codeInitial = String(projCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${name}`;
      }
      return name;
    }
    return "-";
  };

  const formatQty3 = (val) => {
    if (val === null || val === undefined || val === "") return "0.000";
    return Number(val).toFixed(3);
  };

  useEffect(() => {

    const delayDebounce = setTimeout(() => {

      setCurrentPage(1);
      setSearch(searchText);

    }, 400);

    return () => clearTimeout(delayDebounce);

  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await CompanyService.getActiveCompanies();
        setCompanies(res?.data || res || []);
      } catch (err) {
        console.error("Failed to load companies", err);
      }
    };
    loadCompanies();
  }, []);


  useEffect(() => {
    if (permission === null) return;

    if (permission.canView !== 1) {
      setLoading(false);
      return;
    }

    const loadTransfers = async () => {

      try {

        setLoading(true);

        const res = await stockTransferService.getInTransit(
          companyId,
          1,
          1000,
          search,
          statusFilter,
          companyFilter === "ALL" ? "" : companyFilter
        );

        console.log("API RESPONSE:", res);

        const rows = Array.isArray(res?.data) ? res?.data : [];

        setTransfers([...rows].sort((a, b) => b.id - a.id));
        setPageCount(Math.max(1, res?.pagination?.totalPages || 1));
        setSelectedId(null);

      } catch (err) {

        console.error(err);

        showMessage("Failed to load transfers");

      } finally {

        setLoading(false);

      }

    };

    loadTransfers();

  }, [companyId, currentPage, permission, search, statusFilter, companyFilter]);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setTransfers(allTransfers.slice(start, end));
  }, [allTransfers, currentPage]);

  const loadPopupDetails = async (id) => {
    try {
      setPopupLoading(true);
      const res = await stockTransferService.getById(id);
      const data = res?.data?.data || res?.data || res;
      setPopupHeader(data);
      setPopupDetails(data?.stockTransferDetails || []);
    } catch (err) {
      console.error("Popup detail load failed", err);
      showMessage("Failed to load transfer details");
      setPopupHeader(null);
      setPopupDetails([]);
    } finally {
      setPopupLoading(false);
    }
  };

  const handleReceive = () => {

    if (!selectedId)
      return showMessage("Please select one transfer to receive.");

    navigate(`/cpnl/Stocktransfer/receive/${selectedId}`);

  };

  const openViewPopup = async () => {
    if (!selectedId) return showMessage("Please select one transfer to view.");
    setShowPopup(true);
    await loadPopupDetails(selectedId);
  };

  const confirmReject = async () => {

    if (!rejectNotes.trim())
      return showMessage("Please enter reject notes");

    try {

      const employeeId = StorageService.loginData().employeeId;

      await stockTransferService.rejectTransfer(selectedId, {
        employeeId,
        notes: rejectNotes
      });

      showMessage("Transfer rejected successfully", "success");

      setShowRejectModal(false);
      setRejectNotes("");

      setTimeout(() => window.location.reload(), 800);

    } catch (err) {

      console.error(err);
      showMessage("Failed to reject transfer");

    }
  };

  const handleReject = () => {

    if (!selectedId)
      return showMessage("Please select one transfer to reject.");

    const transfer = transfers.find(t => t.id === selectedId);

    if (!transfer || transfer.status !== "intransit")
      return showMessage("Only InTransit transfers can be rejected");

    setShowRejectModal(true);
  };

  if (permission === null) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!permission || permission.canView !== 1) {
    return <UnAuthorized />;
  }

  return (
    <>
      <Breadcrumb
        pageTitle="Receive Stock Transfer"
        secondTitle="Inventory"
      />

      <div className="card p-3 bg-white shadow-sm">

        {/* {message && (
          <div
            className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"
              } d-flex justify-content-between align-items-center w-100 mb-2 p-2`}
          >
            <span>{message}</span>

            <button
              type="button"
              className="btn-close"
              onClick={closeMessage}
            />
          </div>
        )} */}


        <MessageDialog
          message={message}
          type={msgType}
          setMessage={setMessage}
          setMsgType={setMsgType}
        />


        <div className="mb-3 pb-2 border-bottom d-flex align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">

            <button
              className="d-inline-flex btn btn-primary"
              onClick={openViewPopup}
              disabled={!selectedId}
            >
              <i className="ti ti-eye me-1"></i>
              View
            </button>

            <select
              className="form-select w-auto"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="INTRANSIT">In Transit</option>
              <option value="RECEIVED">Received</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <select
              className="form-select w-auto"
              value={companyFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setCompanyFilter(e.target.value);
              }}
            >
              <option value="ALL">All From Companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName}
                </option>
              ))}
            </select>

          </div>

          <div className="d-flex align-items-center ms-auto">
            <div className="position-relative">

              <input
                type="text"
                className="form-control pe-5"
                placeholder="Search Voucher/Project/Vehicle"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "350px" }}
              />

              <i
                className="ti ti-search position-absolute"
                style={{
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d"
                }}
              ></i>

            </div>
          </div>
        </div>

        <div className="table-responsive">

          <table className="table table-striped table-bordered mb-0">

            <thead className="table-light">

              <tr>
                <th>Select</th>
                <th>Voucher No</th>
                <th>Transfer Date</th>
                <th>From Company</th>
                <th>From Project</th>
                <th>To Project</th>
                <th>From Store</th>
                <th>To Store</th>
                <th>Status</th>
              </tr>

            </thead>

            <tbody>

              {loading ? (

                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <Loading />
                  </td>
                </tr>

              ) : transfers.length > 0 ? (

                transfers.map((t) => (

                  <tr key={t.id}>

                    <td>
                      <input
                        type="radio"
                        name="transferSelect"
                        checked={selectedId === t.id}
                        onChange={() => setSelectedId(t.id)}
                      />
                    </td>
                    <td>{t.vocherNo}</td>

                    <td>{formatDate(t.transferDate)}</td>

                    <td>{t.fromCompanyName}</td>

                    <td>
                      {formatProject(t.fromProjectName, t.fromProjectCode, t.fromProjectCategoryName)}
                    </td>

                    <td>
                      {formatProject(t.toProjectName, t.toProjectCode, t.toProjectCategoryName)}
                    </td>

                    <td>{t.fromStoreName}</td>

                    <td>{t.toStoreName}</td>


                    <td>

                      <span className={`badge ${t.status === "intransit" ? "bg-warning" :
                        t.status === "received" ? "bg-success" :
                          t.status === "cancelled" ? "bg-dark" :
                            "bg-danger"
                        }`}>
                        {t.status}
                      </span>

                    </td>



                  </tr>

                ))

              ) : (

                <tr>
                  <td colSpan="9" className="text-center text-muted">
                    No InTransit transfers found
                  </td>
                </tr>

              )}

            </tbody>

          </table>

          <ReactPaginate
            forcePage={Math.max(0, Math.min(currentPage - 1, Math.max(pageCount - 1, 0)))}
            onPageChange={(e) => setCurrentPage(e.selected + 1)}
            pageCount={Math.max(1, pageCount)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            previousLabel="< Previous"
            nextLabel="Next >"
            containerClassName="pagination-container"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            nextClassName="page-item"
            activeClassName="active"
          />
        </div>

      </div>
      {showPopup && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Transfer Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPopup(false)}
                />
              </div>

              <div className="modal-body">
                {popupLoading ? (
                  <div className="text-center p-4">
                    <Loading />
                  </div>
                ) : (
                  <>
                    <div className="row mb-3">
                      {/* 1. Transfer Date & 2. Voucher No (Bold) */}
                      <div className="col-md-6">
                        <strong>Transfer Date:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{formatDate(popupHeader?.transferDate || selectedTransfer?.transferDate)}</span>
                      </div>
                      <div className="col-md-6">
                        <strong>Voucher No:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{popupHeader?.vocherNo || selectedTransfer?.vocherNo || "-"}</span>
                      </div>

                      {/* 3. From Company & 4. To Company */}
                      <div className="col-md-6 mt-2">
                        <strong>From Company:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{popupHeader?.fromCompanyName || selectedTransfer?.fromCompanyName || "-"}</span>
                      </div>
                      <div className="col-md-6 mt-2">
                        <strong>To Company:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{popupHeader?.toCompanyName || selectedTransfer?.toCompanyName || "-"}</span>
                      </div>

                      {/* Remaining fields */}
                      <div className="col-md-6 mt-2">
                        <strong>From Project:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{popupHeader ? formatProject(popupHeader.fromProjectName, popupHeader.fromProjectCode, popupHeader.fromProjectCategoryName) : selectedTransfer ? formatProject(selectedTransfer.fromProjectName, selectedTransfer.fromProjectCode, selectedTransfer.fromProjectCategoryName) : "-"}</span>
                      </div>
                      <div className="col-md-6 mt-2">
                        <strong>To Project:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{popupHeader ? formatProject(popupHeader.toProjectName, popupHeader.toProjectCode, popupHeader.toProjectCategoryName) : selectedTransfer ? formatProject(selectedTransfer.toProjectName, selectedTransfer.toProjectCode, selectedTransfer.toProjectCategoryName) : "-"}</span>
                      </div>

                      <div className="col-md-6 mt-2">
                        <strong>From Store:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{popupHeader?.fromStoreName || selectedTransfer?.fromStoreName || "-"}</span>
                      </div>
                      <div className="col-md-6 mt-2">
                        <strong>To Store:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{popupHeader?.toStoreName || selectedTransfer?.toStoreName || "-"}</span>
                      </div>

                      <div className="col-md-6 mt-2">
                        <strong>Status:</strong> {popupHeader?.status || selectedTransfer?.status || "-"}
                      </div>
                      <div className="col-md-6 mt-2">
                        <strong>Remarks / Transfer Note:</strong> {popupHeader?.stockTransferNote || selectedTransfer?.stockTransferNote || "-"}
                      </div>

                      <div className="col-md-6 mt-2">
                        <strong>Created By:</strong> {formatEmployee(popupHeader?.createdByName, popupHeader?.createdByCode)}
                      </div>
                      <div className="col-md-6 mt-2">
                        <strong>Received / Approved By:</strong> {formatEmployee(popupHeader?.receivedByName, popupHeader?.receivedByCode)}
                      </div>

                      <div className="col-md-6 mt-2">
                        <strong>Cancelled By:</strong> {formatEmployee(popupHeader?.cancelledByName, popupHeader?.cancelledByCode)}
                      </div>
                      <div className="col-md-6 mt-2">
                        <strong>Rejected By:</strong> {formatEmployee(popupHeader?.rejectedByName, popupHeader?.rejectedByCode)}
                      </div>

                      {popupHeader?.status === "rejected" && (
                        <div className="col-md-12 mt-2">
                          <strong>Rejection Notes:</strong> {popupHeader?.rejectedNotes || "-"}
                        </div>
                      )}
                    </div>

                    {(() => {
                      const showScrapStoreCol = popupDetails.some(
                        (r) => parseFloat(r.damagedQuantity || 0) > 0
                      );
                      const totalCols = showScrapStoreCol ? 8 : 7;

                      return (
                        <div className="table-responsive" style={{ maxHeight: "150px", overflowY: "auto" }}>
                          <table className="table table-bordered table-striped mb-0">
                            <thead className="table-light sticky-top" style={{ zIndex: 2 }}>
                              <tr>
                                <th>Item Category</th>
                                <th>Item</th>
                                <th>UOM</th>
                                <th>Transfer Qty</th>
                                <th>Received Qty</th>
                                <th>Damaged Qty</th>
                                {showScrapStoreCol && <th>Scrap Store</th>}
                                <th>Missed Qty</th>
                              </tr>
                            </thead>
                            <tbody>
                              {popupDetails.length > 0 ? (
                                popupDetails.map((row, idx) => (
                                  <tr key={idx}>
                                    <td>{row.itemCategoryName || "-"}</td>
                                    <td>{row.itemCode || row.itemName || row.itemDescription || "-"}</td>
                                    <td>{row.UOM || row.uom || "-"}</td>
                                    <td>{formatQty3(row.quantity ?? row.transferQuantity)}</td>
                                    <td>{formatQty3(row.receivedQuantity)}</td>
                                    <td>{formatQty3(row.damagedQuantity)}</td>
                                    {showScrapStoreCol && (
                                      <td>
                                        {parseFloat(row.damagedQuantity || 0) > 0 && row.scrapStoreName ? (
                                          `${row.scrapStoreName}${row.scrapStoreCode ? ` (${row.scrapStoreCode})` : ""}`
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                    )}
                                    <td>{formatQty3(row.lostQuantity)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={totalCols} className="text-center text-muted">
                                    No items found
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              <div className="modal-footer">
                {can("canApprove") && (
                  <button
                    className="btn btn-success me-2"
                    onClick={() => {
                      setShowPopup(false);
                      handleReceive();
                    }}
                    disabled={!isInTransit}
                  >
                    <i className="ti ti-check me-1"></i>
                    Receive
                  </button>
                )}

                {can("canReject") && (
                  <button
                    className="btn btn-danger me-2"
                    onClick={() => {
                      setShowPopup(false);
                      handleReject();
                    }}
                    disabled={!isInTransit}
                  >
                    <i className="ti ti-x me-1"></i>
                    Reject
                  </button>
                )}

                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPopup(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Reject Transfer</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRejectModal(false)}
                />
              </div>

              <div className="modal-body">
                <label>Reject Notes</label>
                <textarea
                  className="form-control"
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Enter reason for rejection"
                />
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRejectModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-danger"
                  onClick={confirmReject}
                >
                  Confirm Reject
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
}